import { CustomColDef, ColumnConfig, RowColumnType } from "./SnGridTypes";
import { SnGridExcelStyleId } from "./SnGridConstants";
import {
  getCodeData,
  fnFormatNumberByPattern,
} from "@shared/utils/CommonUtils";
import {
  DateBoxCellEditor,
  PassWordCellEditor,
  MaskCellEditor,
} from "./SnGridEditors";
import dayjs from "dayjs";
import {
  ITooltipParams,
  ColDef,
  CellStyleFunc,
} from "ag-grid-charts-enterprise";
import ButtonBoxCellEditor from "@shared/components/Grid/ButtonBoxCellEditor";
import { showDialog } from "@widgets/CommonDialog";
import { PopupSettings } from "@widgets/commonpopup/PopupSettings";
import { fnGetDataSet } from "@shared/api/restApi";
import { global } from "@shared/libs/Global";
import ProgressRenderer from "./ProgressRenderer";
import { consumePopupValue, setPopupValue } from "./SnGridPopupFlag";
import { toFindText, renderFindHighlight } from "./SnGridFindHighlight";

// 콤보 드롭다운 항목 텍스트 폭 측정용 캔버스 (재사용)
let comboMeasureCtx: CanvasRenderingContext2D | null = null;

/**
 * SnComboBox 컬럼 드롭다운(agRichSelectCellEditor) 목록 폭 계산
 * - ag-grid는 목록 폭을 셀 폭에 고정하므로 긴 항목 텍스트가 가로로 잘린다.
 * - 셀 폭과 가장 긴 항목 텍스트 폭 중 큰 값을 돌려주어 valueListMaxWidth 로 사용한다.
 * @param params cellEditorParams 인자 (column, eGridCell 사용)
 * @param labels 드롭다운에 표시될 항목 텍스트 목록
 */
const getComboListWidth = (params: any, labels: string[]): number => {
  const cellWidth: number =
    params.column?.getActualWidth?.() ?? params.eGridCell?.offsetWidth ?? 0;
  try {
    if (!comboMeasureCtx) {
      comboMeasureCtx = document.createElement("canvas").getContext("2d");
    }
    if (!comboMeasureCtx) return cellWidth;

    // 셀과 같은 글꼴로 측정
    const style = params.eGridCell ? getComputedStyle(params.eGridCell) : null;
    comboMeasureCtx.font = style
      ? `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`
      : "13px sans-serif";

    let maxText = 0;
    for (const label of labels) {
      maxText = Math.max(
        maxText,
        comboMeasureCtx.measureText(String(label ?? "")).width
      );
    }

    // 좌우 padding(--ag-spacing) + 스크롤바 + 테두리 여유분
    const needed = Math.ceil(maxText) + 40;
    // 화면 폭의 60%를 넘지 않도록 제한
    const limit = Math.floor(window.innerWidth * 0.6);
    return Math.max(cellWidth, Math.min(needed, limit));
  } catch {
    return cellWidth;
  }
};

// 유형별 컬럼 설정
const getColumnTypeConfig = async (
  column: ColumnConfig
): Promise<Partial<CustomColDef>> => {
  switch (column.tp_column) {
    case "SnTextBox":
      return {
        cellEditor: "agTextCellEditor",
        filter: "agTextColumnFilter",
        editable: (params: any) => {
          const rowData = params.data;
          let cond = params.colDef.columnConfig.dc_editcon;
          if (cond == null || cond == "") return true;
          if (rowData.state === "insert") {
            cond = cond.replace("new", "false");
          } else {
            cond = cond.replace("new", "true");
          }
          Object.keys(rowData)
            .sort((a, b) => b.length - a.length)
            .forEach((key) => {
              cond = cond.replaceAll(key, rowData[key]);
            });
          return !new Function(`return (${cond})`)();
        },
      };

    case "SnNumberBox":
      return {
        cellEditor: "agNumberCellEditor",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => {
          const colDef = params.colDef as any;
          const tp_format = colDef.columnConfig?.tp_format; // 예: "#,##0.00" 또는 "#,###"

          if (
            params.value === null ||
            params.value === undefined ||
            params.value === ""
          )
            return "";

          const numValue = Number(params.value);
          if (isNaN(numValue)) return params.value;

          if (tp_format) {
            return fnFormatNumberByPattern(numValue, tp_format);
          }

          return numValue.toLocaleString();
        },
        cellStyle: (params) => {
          return { textAlign: "right" }; // 오른쪽 정렬
        },
        editable: (params: any) => {
          const rowData = params.data;
          let cond = params.colDef.columnConfig.dc_editcon;
          if (cond == null || cond == "") return true;
          if (rowData.state === "insert") {
            cond = cond.replace("new", "false");
          } else {
            cond = cond.replace("new", "true");
          }
          Object.keys(rowData)
            .sort((a, b) => b.length - a.length)
            .forEach((key) => {
              cond = cond.replaceAll(key, rowData[key]);
            });
          return !new Function(`return (${cond})`)();
        },
        valueParser: (params) => {
          let val = params.newValue;
          if (val == null || val === "") return null;

          if (typeof val === "string") {
            val = val.replace(/,/g, "");
          }

          const num = Number(val);
          return isNaN(num) ? null : num;
        },
        valueSetter: (params) => {
          const field = params.colDef.field!;
          params.data[field] = params.newValue;
          return true;
        },
      };

    case "SnDateBox": {
      return {
        // 26.09.23 kdy Excel 내보내기 시 날짜 셀 스타일 매칭용 (값이 있을 때만 적용해 빈 셀 오류 방지)
        cellClass: (params) =>
          params.value ? SnGridExcelStyleId.date : undefined,
        cellEditor: DateBoxCellEditor,
        valueFormatter: (params) => {
          const value = params.value;
          return value ? dayjs(value, "YYYYMMDD").format("YYYY/MM/DD") : "";
        },
        valueParser: (params) => {
          const val = params.newValue;
          if (!val) return "";

          const normalized = val.replace(/[^0-9]/g, "");
          if (normalized.length === 8) return normalized;

          return "";
        },
        cellStyle: {
          textAlign: "left", // 좌측 정렬
        },
        editable: (params: any) => {
          const rowData = params.data;
          let cond = params.colDef.dc_editcon;
          if (cond == null || cond == "") return true;
          if (rowData.state === "insert") {
            cond = cond.replace("new", "false");
          } else {
            cond = cond.replace("new", "true");
          }
          Object.keys(rowData)
            .sort((a, b) => b.length - a.length)
            .forEach((key) => {
              cond = cond.replaceAll(key, rowData[key]);
            });
          return !new Function(`return (${cond})`)();
        },
        valueSetter: (params) => {
          const field = params.colDef.field!;
          let val = params.newValue;

          // 1. 빈값 처리 (허용 여부는 선택)
          if (!val) return false;

          // 2. 포맷별 처리
          let parsed = "";
          val = String(val).trim();
          if (/^\d{8}$/.test(val)) {
            // 20250301
            parsed = val;
          } else if (/^\d{4}[-/]\d{2}[-/]\d{2}$/.test(val)) {
            // 2025-03-01, 2025/03/01
            parsed = val.replace(/[-/]/g, "");
          } else {
            // 기타
            const normalized = val.replace(/[^0-9]/g, "");
            if (normalized.length === 8) {
              parsed = normalized;
            } else {
              return false;
            }
          }

          // 3. 길이 체크 (YYYYMMDD)
          if (parsed.length !== 8) return false;

          // 4. 날짜 유효성 체크
          if (!dayjs(parsed, "YYYYMMDD", true).isValid()) return false;

          // 5. 정상일 때만 데이터 반영
          params.data[field] = parsed;
          return true;
        },
      };
    }

    case "SnDateMonthBox": {
      return {
        // 26.09.23 kdy Excel 내보내기 시 날짜 셀 스타일 매칭용 (값이 있을 때만 적용해 빈 셀 오류 방지)
        cellClass: (params) =>
          params.value ? SnGridExcelStyleId.month : undefined,
        cellEditor: DateBoxCellEditor,
        valueFormatter: (params) => {
          const value = params.value;
          return value ? dayjs(value, "YYYYMM").format("YYYY/MM") : "";
        },
        valueParser: (params) => {
          const val = params.newValue;
          if (!val) return "";

          const normalized = val.replace(/[^0-9]/g, "");
          if (normalized.length === 8) return normalized;

          return "";
        },
        cellStyle: {
          textAlign: "center", // 가운데 정렬
        },
        editable: (params: any) => {
          const rowData = params.data;
          let cond = params.colDef.dc_editcon;
          if (cond == null || cond == "") return true;
          if (rowData.state === "insert") {
            cond = cond.replace("new", "false");
          } else {
            cond = cond.replace("new", "true");
          }
          Object.keys(rowData)
            .sort((a, b) => b.length - a.length)
            .forEach((key) => {
              cond = cond.replaceAll(key, rowData[key]);
            });
          return !new Function(`return (${cond})`)();
        },
        valueSetter: (params) => {
          const field = params.colDef.field!;
          let val = params.newValue;

          // 1. 빈값 처리 (허용 여부는 선택)
          if (!val) return false;

          // 2. 포맷별 처리
          let parsed = "";
          val = String(val).trim();
          if (/^\d{8}$/.test(val)) {
            // 20250301
            parsed = val;
          } else if (/^\d{4}[-/]\d{2}[-/]\d{2}$/.test(val)) {
            // 2025-03-01, 2025/03/01
            parsed = val.replace(/[-/]/g, "");
          } else {
            // 기타
            const normalized = val.replace(/[^0-9]/g, "");
            if (normalized.length === 8) {
              parsed = normalized;
            } else {
              return false;
            }
          }

          // 3. 길이 체크 (YYYYMMDD)
          if (parsed.length !== 8) return false;

          // 4. 날짜 유효성 체크
          if (!dayjs(parsed, "YYYYMM", true).isValid()) return false;

          // 5. 정상일 때만 데이터 반영
          params.data[field] = parsed;
          return true;
        },
      };
    }

    case "SnCheckBox": {
      return {
        // cellRenderer: (params: { value: string }) =>
        //   params.value === "Y" ? "✔" : "✖", // 렌더링 시 체크/언체크 표시
        // cellRenderer: "agCheckboxCellRenderer", // 기본 체크박스 렌더러 사용
        cellRendererSelector: (params: any) => {
          if (params.node?.rowPinned) {
            return { component: "" }; // footer는 아무것도 안 보여줌
          }
          return { component: "agCheckboxCellRenderer" };
        },
        cellEditor: "agCheckboxCellEditor", // 체크박스 편집기 사용
        cellEditorParams: {
          trueValue: "Y", // 체크된 값
          falseValue: "N", // 체크 해제된 값
        },
        valueSetter: (params: any) => {
          // 체크 상태에 따라 데이터에 Y/N 값 설정
          const field = params.colDef.field;
          if (params.colDef && field) {
            const oldValue = params.oldValue;
            const val = params.newValue;

            // 1. null/undefined 처리
            if (val === null || val === undefined || val === "") return false;

            // 2. 허용 값만 처리
            const normalized = String(val).trim().toUpperCase();
            if (
              normalized === "Y" ||
              normalized === "TRUE" ||
              normalized === "1"
            ) {
              if (oldValue) return false;
              params.data[field] = "Y";
              return true;
            }
            if (
              normalized === "N" ||
              normalized === "FALSE" ||
              normalized === "0"
            ) {
              if (!oldValue) return false;
              params.data[field] = "N";
              return true;
            }

            // 이상 값 차단
            return false;
          }
          return false;
        },
        valueGetter: (params: any) => {
          if (params.node?.rowPinned) return undefined;
          // 데이터에서 Y/N 값을 기반으로 체크 상태 반환
          return params.data[params.colDef.field] === "Y";
        },
        cellStyle: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
        onCellDoubleClicked: (params) => {
          params.api.stopEditing();
        },
        editable: (params: any) => {
          const rowData = params.data;
          let cond = params.colDef.columnConfig.dc_editcon;
          if (cond == null || cond == "") return true;
          if (rowData.state === "insert") {
            cond = cond.replace("new", "false");
          } else {
            cond = cond.replace("new", "true");
          }
          Object.keys(rowData)
            .sort((a, b) => b.length - a.length)
            .forEach((key) => {
              cond = cond.replaceAll(key, rowData[key]);
            });
          return !new Function(`return (${cond})`)();
        },
      };
    }

    case "SnComboBox": {
      const dataSource = Array.isArray(column.js_combo)
        ? column.js_combo
        : typeof column.js_combo === "string"
          ? JSON.parse(column.js_combo)
          : [];
      return {
        cellEditor: "agRichSelectCellEditor", // Rich Select 에디터 사용
        cellEditorParams: (params: any) => {
          const key = params.colDef?.colId ?? params.colDef?.field ?? "";
          const base = params.context?.comboCache?.[key] ?? dataSource;

          // dc_filter 적용된 드롭다운 항목 목록
          const getFilteredData = (): any[] => {
            let filData = column.dc_filter || "";
            if (!filData) return base;

            // [필드]는 콤보 코드(orgData) 참조, {필드}는 행 값. 행에 키가 없어도 미치환 상태로 eval되지 않게 한다.
            const row = params.data ?? {};
            filData = filData
              .replace(/\[(\w+)\]/g, "orgData.$1")
              .replace(/\{(\w+)\}/g, (_: string, k: string) =>
                String(row[k] ?? "")
              );

            return base.filter((orgData: any) =>
              new Function("orgData", `return ${filData}`)(orgData)
            );
          };
          const filteredData = getFilteredData();

          return {
            values: () => filteredData.map((item: { code: any }) => item.code),
            // 드롭다운 폭: 셀 폭 이상, 가장 긴 "name [code]" 텍스트가 잘리지 않는 폭
            valueListMaxWidth: getComboListWidth(
              params,
              filteredData.map(
                (item: { code: any; name: any }) =>
                  `${item.name} [${item.code}]`
              )
            ),
            //// 25.09.12 agRichSelectCellEditor에 포멧을 사용하지 않고 렌더로 사용하여 name, code 구조로 포멧을 지정할 수 있음.
            // formatValue: (value: string) => {
            //   const selected = dataSource.find(
            //     (item: { code: string }) => item.code === value
            //   );
            //   // return selected ? selected.name : value;
            //   return selected ? `${selected.name} [${selected.code}]` : value;
            // },
            cellRenderer: (editorParams: { value: string }) => {
              const selected = base.find(
                (item: { code: string }) =>
                  String(item.code) === String(editorParams.value)
              );
              return selected
                ? `${selected.name} [${selected.code}]`
                : editorParams.value;
            },
          };
        },
        cellRenderer: (params: any) => {
          const key = params.colDef?.colId ?? params.colDef?.field ?? "";
          const base = params.context?.comboCache?.[key] ?? dataSource;
          // 셀에 name 값 표시 (+ 찾기 하이라이트)
          const selected = base.find(
            (item: { code: any }) => String(item.code) === String(params.value)
          );
          return renderFindHighlight(
            params,
            selected ? selected.name : params.value
          );
        },
        // 찾기(Ctrl+F): 화면에 보이는 텍스트(name, 없으면 셀에 그대로 보이는 code)로만 검색
        getFindText: (params: any) => {
          const key = params.colDef?.colId ?? params.colDef?.field ?? "";
          const base = params.context?.comboCache?.[key] ?? dataSource;
          const selected = base.find(
            (item: { code: any }) => String(item.code) === String(params.value)
          );
          return toFindText(selected ? selected.name : params.value, "");
        },
        valueGetter: (params: any) => {
          // 데이터에서 code 값 반환
          if (params.colDef && params.colDef.field) {
            return params.data?.[params.colDef.field] ?? "";
          }
          return "";
        },
        valueSetter: (params) => {
          const field = params.colDef.field;
          if (!field) return false;

          const val = params.newValue;
          if (val === null || val === undefined || val === "") return false;

          const key = params.colDef?.colId ?? params.colDef?.field ?? "";
          const base = params.context?.comboCache?.[key] ?? dataSource;

          const normalized = String(val).trim();

          const found = base.find(
            (item: any) =>
              String(item.code) === normalized ||
              String(item.name) === normalized
          );
          if (!found) return false; // 이상한 값

          // 정상 값만 저장
          params.data[field] = found.code;
          return true;
        },
        editable: (params: any) => {
          const rowData = params.data;
          let cond = params.colDef.columnConfig.dc_editcon;
          if (cond == null || cond == "") return true;
          if (rowData.state === "insert") {
            cond = cond.replace("new", "false");
          } else {
            cond = cond.replace("new", "true");
          }
          Object.keys(rowData)
            .sort((a, b) => b.length - a.length)
            .forEach((key) => {
              cond = cond.replaceAll(key, rowData[key]);
            });
          return !new Function(`return (${cond})`)();
        },
        // 필수값 표시 스타일
        // cellStyle: (params) => {
        //   if (column.yn_required === "Y" && !params.node?.rowPinned) {
        //     return { backgroundColor: "#fff3cd" };
        //   }
        // },
      };
    }

    case "SnCodeBox":
      return {
        cellRenderer: (params: any) => {
          const rawValue = params.data?.[column.cd_column];
          if (column.cd_displaycolumn) {
            const displayValue = params.data?.[column.cd_displaycolumn] || "";
            return renderFindHighlight(
              params,
              params.focused ? rawValue : displayValue
            );
          } else {
            return renderFindHighlight(params, rawValue || "");
          }
        },
        getFindText: (params: any) => {
          if (!column.cd_displaycolumn) return params.value ?? "";
          return params.data?.[column.cd_displaycolumn] || "";
        },
        filter: true,
        filterValueGetter: (params: any) =>
          params.data?.[params.colDef.field] ?? "",
        valueGetter: (params) => {
          // 데이터에서 code 값 반환
          if (params.colDef && params.colDef.field) {
            return params.data?.[params.colDef.field] ?? "";
          }
          return "";
        },
        valueSetter: (params) => {
          // 코드에 앞뒤 공백은 없다. 붙은 채로 두면 조회가 빗나가고 그대로 저장된다.
          const trimmed =
            typeof params.newValue === "string"
              ? params.newValue.trim()
              : params.newValue;
          if (params.newValue !== undefined) {
            if (params.colDef && params.colDef.field) {
              params.data[params.colDef.field] = trimmed;
              return true;
            }
          } else if (params.oldValue !== undefined) {
            if (params.colDef && params.colDef.field) {
              params.data[params.colDef.field] = params.oldValue;
              return true;
            }
          }
          return false;
        },
        // ag-grid의 onCellValueChanged 대신 handleInternalCellValueChanged가 source를 담아 호출
        codeValueChanged: async (params: any) => {
          if (!params.node || !column.cd_popup) return;
          if (params.colDef.field !== column.cd_column) return;
          if (params.newValue === params.oldValue) return;
          // 26.08.21 ysw 도움창(PopupHelper)에서 들어온 값은 이미 확정된 값이므로 재조회하지 않는다.
          // (setCodeBoxValue가 행의 cd_biz로 다시 필터링해 0건이 되면 clearRelatedColumns가
          //  방금 고른 코드를 지워버린다 - 타사업장 사원 선택 후 필수값 오류의 원인)
          if (
            consumePopupValue(params.node, params.colDef.field, params.newValue)
          )
            return;
          if (
            String(params.node.data?.[params.colDef.field] ?? "").trim() !==
            String(params.newValue ?? "").trim()
          )
            return;
          // 화면 코드가 "noEvent"/"noChange"로 넣은 코드는비어 있는 컬럼(nm_item 등)만 채운다.
          // 사용자 직접 입력(source "edit")은 기존대로 전부 갱신.
          const keepExisting =
            params.source === "noEvent" || params.source === "noChange";

          const popupCols = PopupSettings[column.cd_popup].columns;
          // 다른 컬럼의 필드는 code/name(0,1번)이 아니면
          // 지울 때는 항상, 적용할 때는 상위 컬럼이 잠겼을 때만 그대로 둔다
          const parentOf = (onlyLocked: boolean) =>
            getParentFields(
              params.api,
              params.node,
              params.colDef.field,
              popupCols,
              onlyLocked
            );
          const parentAll = parentOf(false);
          const parentLocked = parentOf(true);
          const keepOnClear = (popupCol: string, target: string) =>
            popupCols.indexOf(popupCol) > 1 && parentAll.has(target);
          const keepOnApply = (popupCol: string, target: string) =>
            popupCols.indexOf(popupCol) > 1 && parentLocked.has(target);
          const mappings = getCodeBoxColumnMappings(
            column,
            popupCols,
            params.colDef.field,
            params.node.data
          );
          // 26.08.21 ysw 그리드 컬럼설정에 없는 필드는 setDataValue가 무동작이어서
          // 코드는 비었는데 표시용 이름만 남는 유령값이 생긴다. 데이터에 직접 반영한다.
          const setRelatedValue = (target: string, value: any) => {
            if (params.api.getColumn(target)) {
              params.node!.setDataValue(target, value);
            } else if (params.node!.data) {
              params.node!.data[target] = value;
            }
          };
          const clearRelatedColumns = () =>
            mappings.forEach(({ popupCol, target }) => {
              if (keepOnClear(popupCol, target)) return;
              if (params.node!.data[target] === undefined) return;
              if (params.node!.data[target] === "") return;
              setRelatedValue(target, "");
            });

          // 코드를 지운 것과 공백만 남은 것은 같게 본다.
          // 셀에 포커스를 두고 스페이스바를 누르면 그 한 글자로 편집이 시작돼(SnGrid의
          // handleKeyboardNavigation) 공백만 든 채로 확정된다.
          const typedCode = String(params.newValue ?? "").trim();
          if (typedCode === "") {
            clearRelatedColumns();
            params.api.refreshCells({
              rowNodes: [params.node],
              columns: [column.cd_column],
              force: true,
            });
            return;
          }

          const refreshCodeCell = () =>
            params.api.refreshCells({
              rowNodes: [params.node],
              columns: [column.cd_column],
              force: true,
            });

          const result = await setCodeBoxValue(column, params, typedCode);
          // 0건·여러 건: 사용자가 직접 친 값이면 비우지 않고 그리드에 넘긴다.
          // Enter/Tab 이면 그 글자로 도움창을 띄우고, 고르지 않거나 그 외(클릭으로 빠져나감 등)는
          // 치기 전 값으로 되돌린다 (SnGrid handleCodeTyped)
          if (result.length !== 1 && params.source === "edit") {
            return { needPopup: true };
          }
          if (result.length === 0) {
            if (!keepExisting) clearRelatedColumns();
          } else if (result.length === 1) {
            const row = result[0];

            mappings.forEach(({ popupCol, target }) => {
              if (target === params.colDef.field) {
                // 이름·코드 일부로 1건을 찾았으면 칸에 친 글자 대신 찾은 코드로 바꾼다
                const found = row[popupCol];
                if (found != null && String(found) !== typedCode)
                  setPopupValue(params.node, target, found);
                return;
              }
              if (keepOnApply(popupCol, target)) return;
              const cur = params.node!.data?.[target];
              if (
                keepExisting &&
                cur !== undefined &&
                cur !== null &&
                cur !== ""
              )
                return;
              setRelatedValue(target, row[popupCol] ?? "");
            });
          }
          refreshCodeCell();
        },
        editable: (params: any) => {
          const rowData = params.data;
          let cond = params.colDef.columnConfig.dc_editcon;
          if (cond == null || cond == "") return true;
          if (rowData.state === "insert") {
            cond = cond.replace("new", "false");
          } else {
            cond = cond.replace("new", "true");
          }
          Object.keys(rowData)
            .sort((a, b) => b.length - a.length)
            .forEach((key) => {
              cond = cond.replaceAll(key, rowData[key]);
            });
          return !new Function(`return (${cond})`)();
        },
      };

    case "SnMultiCodeBox":
      return {
        cellRenderer: (params: any) =>
          renderFindHighlight(params, params.data?.[column.cd_column]),
        valueGetter: (params) => {
          // 데이터에서 code 값 반환
          if (params.colDef && params.colDef.field) {
            return params.data?.[params.colDef.field] ?? "";
          }
          return "";
        },
        valueSetter: (params) => {
          // 코드에 앞뒤 공백은 없다. 붙은 채로 두면 조회가 빗나가고 그대로 저장된다.
          const trimmed =
            typeof params.newValue === "string"
              ? params.newValue.trim()
              : params.newValue;
          if (params.newValue !== undefined) {
            if (params.colDef && params.colDef.field) {
              params.data[params.colDef.field] = trimmed;
              return true;
            }
          } else if (params.oldValue !== undefined) {
            if (params.colDef && params.colDef.field) {
              params.data[params.colDef.field] = params.oldValue;
              return true;
            }
          }
          return false;
        },
        // ag-grid의 onCellValueChanged 대신 handleInternalCellValueChanged가 source를 담아 호출
        codeValueChanged: async (params: any) => {
          if (!params.node || !column.cd_popup) return;
          if (params.colDef.field !== column.cd_column) return;
          if (params.newValue === params.oldValue) return;
          // 26.08.21 ysw 도움창(PopupHelper)에서 들어온 값은 이미 확정된 값이므로 재조회하지 않는다.
          // (setCodeBoxValue가 행의 cd_biz로 다시 필터링해 0건이 되면 clearRelatedColumns가
          //  방금 고른 코드를 지워버린다 - 타사업장 사원 선택 후 필수값 오류의 원인)
          if (
            consumePopupValue(params.node, params.colDef.field, params.newValue)
          )
            return;
          if (
            String(params.node.data?.[params.colDef.field] ?? "").trim() !==
            String(params.newValue ?? "").trim()
          )
            return;
          // 화면 코드가 "noEvent"/"noChange"로 넣은 코드는비어 있는 컬럼(nm_item 등)만 채운다.
          // 사용자 직접 입력(source "edit")은 기존대로 전부 갱신.
          const keepExisting =
            params.source === "noEvent" || params.source === "noChange";

          const popupCols = PopupSettings[column.cd_popup].columns;
          // 다른 컬럼(예: 품목)의 필드는 code/name(0,1번)이 아니면
          // 지울 때는 항상, 적용할 때는 상위 컬럼이 잠겼을 때만 그대로 둔다
          const parentOf = (onlyLocked: boolean) =>
            getParentFields(
              params.api,
              params.node,
              params.colDef.field,
              popupCols,
              onlyLocked
            );
          const parentAll = parentOf(false);
          const parentLocked = parentOf(true);
          const keepOnClear = (popupCol: string, target: string) =>
            popupCols.indexOf(popupCol) > 1 && parentAll.has(target);
          const keepOnApply = (popupCol: string, target: string) =>
            popupCols.indexOf(popupCol) > 1 && parentLocked.has(target);
          const mappings = getCodeBoxColumnMappings(
            column,
            popupCols,
            params.colDef.field,
            params.node.data
          );
          // 26.08.21 ysw 그리드 컬럼설정에 없는 필드는 setDataValue가 무동작이어서
          // 코드는 비었는데 표시용 이름만 남는 유령값이 생긴다. 데이터에 직접 반영한다.
          const setRelatedValue = (target: string, value: any) => {
            if (params.api.getColumn(target)) {
              params.node!.setDataValue(target, value);
            } else if (params.node!.data) {
              params.node!.data[target] = value;
            }
          };
          const clearRelatedColumns = () =>
            mappings.forEach(({ popupCol, target }) => {
              if (keepOnClear(popupCol, target)) return;
              if (params.node!.data[target] === undefined) return;
              if (params.node!.data[target] === "") return;
              setRelatedValue(target, "");
            });

          // 코드를 지운 것과 공백만 남은 것은 같게 본다 (SnCodeBox와 같은 이유)
          const typedCode = String(params.newValue ?? "").trim();
          if (typedCode === "") {
            clearRelatedColumns();
            params.api.refreshCells({
              rowNodes: [params.node],
              columns: [column.cd_column],
              force: true,
            });
            return;
          }

          const refreshCodeCell = () =>
            params.api.refreshCells({
              rowNodes: [params.node],
              columns: [column.cd_column],
              force: true,
            });

          const result = await setCodeBoxValue(column, params, typedCode);
          // 0건·여러 건: SnCodeBox와 같다 (SnGrid handleCodeTyped)
          if (result.length !== 1 && params.source === "edit") {
            return { needPopup: true };
          }
          if (result.length === 0) {
            if (!keepExisting) clearRelatedColumns();
          } else if (result.length === 1) {
            const row = result[0];

            mappings.forEach(({ popupCol, target }) => {
              if (target === params.colDef.field) {
                // 이름·코드 일부로 1건을 찾았으면 칸에 친 글자 대신 찾은 코드로 바꾼다
                const found = row[popupCol];
                if (found != null && String(found) !== typedCode)
                  setPopupValue(params.node, target, found);
                return;
              }
              if (keepOnApply(popupCol, target)) return;
              const cur = params.node!.data?.[target];
              if (
                keepExisting &&
                cur !== undefined &&
                cur !== null &&
                cur !== ""
              )
                return;
              setRelatedValue(target, row[popupCol] ?? "");
            });
          }
          refreshCodeCell();
        },
        editable: (params: any) => {
          const rowData = params.data;
          let cond = params.colDef.columnConfig.dc_editcon;
          if (cond == null || cond == "") return true;
          if (rowData.state === "insert") {
            cond = cond.replace("new", "false");
          } else {
            cond = cond.replace("new", "true");
          }
          Object.keys(rowData)
            .sort((a, b) => b.length - a.length)
            .forEach((key) => {
              cond = cond.replaceAll(key, rowData[key]);
            });
          return !new Function(`return (${cond})`)();
        },
      };

    case "SnButtonBox":
      return {
        ...column,
        headerComponent: ButtonBoxCellEditor,
        headerClass: "sn-button-header",
        cellRendererSelector: (params) => {
          if (params.node?.rowPinned) {
            return { component: "" };
          }
          return { component: ButtonBoxCellEditor };
        },
        cellRenderer: ButtonBoxCellEditor,
        cellStyle: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
      };

    case "SnPasswordBox": {
      return {
        cellEditor: PassWordCellEditor, // 커스텀 마스크 에디터 사용
        cellEditorParams: {
          maskType: "password", // 마스크 유형 전달
        },
        cellRenderer: (params: any) => {
          const value = params.value;
          // if (!value) return "";
          return "••••••••••";
        },
        // 찾기(Ctrl+F): 마스킹된 값이 검색/카운트에 잡히지 않도록 제외
        getFindText: () => "",
        editable: (params: any) => {
          const rowData = params.data;
          let cond = params.colDef.columnConfig.dc_editcon;
          if (cond == null || cond == "") return true;
          if (rowData.state === "insert") {
            cond = cond.replace("new", "false");
          } else {
            cond = cond.replace("new", "true");
          }
          Object.keys(rowData)
            .sort((a, b) => b.length - a.length)
            .forEach((key) => {
              cond = cond.replaceAll(key, rowData[key]);
            });
          return !new Function(`return (${cond})`)();
        },
      };
    }

    case "SnMaskBox":
      return {
        cellEditor: MaskCellEditor,
        valueFormatter: (params) => {
          const colDef = params.colDef as any;
          const format = colDef.columnConfig?.tp_format;
          const value = params.value;

          if (!value || !format) return value || "";

          const digits = String(value).replace(/[^0-9]/g, "");
          let result = "";
          let digitIndex = 0;

          for (
            let i = 0;
            i < format.length && digitIndex < digits.length;
            i++
          ) {
            if (format[i] === "#") {
              result += digits[digitIndex];
              digitIndex++;
            } else {
              result += format[i];
            }
          }
          return result;
        },

        valueSetter: (params: any) => {
          const colDef = params.colDef as any;
          const format = colDef.columnConfig?.tp_format;
          const newValue = params.newValue;

          if (!newValue) return true;
          const requiredLength = (format.match(/#/g) || []).length;

          const inputLength = String(newValue).replace(/[^0-9]/g, "").length;

          if (inputLength !== requiredLength) {
            requestAnimationFrame(() => {
              showDialog("alert", "자리수가 맞지 않습니다");
            });

            return false;
          }

          params.data[colDef.field] = newValue;
          return true;
        },

        editable: (params: any) => {
          const rowData = params.data;
          let cond = params.colDef.columnConfig.dc_editcon;
          if (cond == null || cond == "") return true;
          if (rowData.state === "insert") {
            cond = cond.replace("new", "false");
          } else {
            cond = cond.replace("new", "true");
          }
          Object.keys(rowData)
            .sort((a, b) => b.length - a.length)
            .forEach((key) => {
              cond = cond.replaceAll(key, rowData[key]);
            });
          return !new Function(`return (${cond})`)();
        },
      };

    case "SnChangeBox":
      return {
        editable: (params: any) => {
          const rowData = params.data;
          let cond = params.colDef.columnConfig.dc_editcon;
          if (cond == null || cond == "") return true;
          if (rowData.state === "insert") {
            cond = cond.replace("new", "false");
          } else {
            cond = cond.replace("new", "true");
          }
          Object.keys(rowData)
            .sort((a, b) => b.length - a.length)
            .forEach((key) => {
              cond = cond.replaceAll(key, rowData[key]);
            });
          return !new Function(`return (${cond})`)();
        },

        cellEditorSelector: (params) => {
          const type = params.data.tp_data;

          if (type === "1") {
            return {
              component: "agNumberCellEditor",
            };
          }

          if (type === "3") {
            return {
              component: "agCheckboxCellEditor",
              params: {
                trueValue: "Y",
                falseValue: "N",
              },
            };
          }

          return {
            component: "agTextCellEditor",
          };
        },

        cellRendererSelector: (params: any) => {
          if (params.data.tp_data === "3") {
            if (params.node?.rowPinned) {
              return { component: "" };
            }
            return { component: "agCheckboxCellRenderer" };
          }
        },

        valueFormatter: (params: any) => {
          if (params.data.tp_data !== "1") return params.value;

          const colDef = params.colDef;
          const tp_format = colDef.columnConfig?.tp_format;

          if (
            params.value === null ||
            params.value === undefined ||
            params.value === ""
          )
            return "";

          const numValue = Number(params.value);
          if (isNaN(numValue)) return params.value;

          if (tp_format) {
            return fnFormatNumberByPattern(numValue, tp_format);
          }

          return numValue.toLocaleString();
        },

        valueGetter: (params: any) => {
          if (params.data.tp_data === "3")
            return params.data[params.colDef.field] === "Y";

          if (params.node?.rowPinned) return undefined;

          return params.data[params.colDef.field];
        },

        valueSetter: (params: any) => {
          if (params.data.tp_data === "3") {
            params.data[params.colDef.field] = params.newValue ? "Y" : "N";
            return true;
          } else {
            params.data[params.colDef.field] = params.newValue;
            return true;
          }
        },

        cellStyle: (params) => {
          if (params.data.tp_data === "1") {
            return {
              textAlign: "right",
              display: "",
              justifyContent: "",
              alignItems: "",
            };
          }

          if (params.data.tp_data === "3") {
            return {
              textAlign: "",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            };
          }
        },

        onCellDoubleClicked: (params: any) => {
          if (params.data.tp_data === "3") {
            params.api.stopEditing();
          }
        },

        filter: (params: any) => {
          return params.data.tp_data === "1" ? "agNumberColumnFilter" : false;
        },
      };
    case "SnProgressBar":
      return {
        filter: "agNumberColumnFilter",
        editable: false,
        cellRenderer: ProgressRenderer,
        cellRendererParams: (params: any) => {
          const colDef = params.colDef;
          const tp_format = colDef.columnConfig?.tp_format;
          console.log(tp_format);
          return {
            format: tp_format,
          };
        },
        aggFunc: undefined,
      };

    default:
      return {
        cellEditor: "agTextCellEditor",
        filter: "agTextColumnFilter",

        editable: (params: any) => {
          const rowData = params.data;
          let cond = params.colDef.columnConfig.dc_editcon;
          if (cond == null || cond == "") return true;
          if (rowData.state === "insert") {
            cond = cond.replace("new", "false");
          } else {
            cond = cond.replace("new", "true");
          }
          Object.keys(rowData)
            .sort((a, b) => b.length - a.length)
            .forEach((key) => {
              cond = cond.replaceAll(key, rowData[key]);
            });
          return !new Function(`return (${cond})`)();
        },
      };
  }
};

// 컬럼 정의 생성 함수
/**
 * 26.10.07 kdy 행별 컬럼 타입
 */
export const getRowTpColumn = (colDef: any, data: any): string | undefined => {
  const config = colDef?.columnConfig;
  if (!config) return undefined;
  if (data && typeof config.tpColumnByRow === "function") {
    const rowType = config.tpColumnByRow(data);
    if (rowType) return rowType;
  }
  return config.tp_column ?? undefined;
};

const ROW_COLUMN_TYPES: RowColumnType[] = ["SnDateBox", "SnTextBox"];

/**
 * tpColumnByRow 가 다른 타입을 돌려주는 행에는 그 타입의 편집기·표시·값 처리를 쓰고,
 * 나머지 행은 원래 tp_column 설정을 그대로 쓴다.
 * (코드박스 컬럼이라도 다른 타입 행에서는 코드 조회(codeValueChanged)를 타지 않는다)
 */
const applyRowTypeOverride = async (colDef: any, column: ColumnConfig) => {
  const typeConfigs: Record<string, any> = {};
  for (const tp of ROW_COLUMN_TYPES) {
    typeConfigs[tp] = await getColumnTypeConfig({ ...column, tp_column: tp });
  }
  // 그 행의 대체 타입 설정. 원래 타입과 같거나 지정이 없으면 undefined (→ 기존 동작)
  const rowConfig = (data: any) => {
    const tp = data ? column.tpColumnByRow?.(data) : undefined;
    return tp && tp !== column.tp_column ? typeConfigs[tp] : undefined;
  };

  const base = {
    valueFormatter: colDef.valueFormatter,
    valueParser: colDef.valueParser,
    valueSetter: colDef.valueSetter,
    cellClass: colDef.cellClass,
    getFindText: colDef.getFindText,
    codeValueChanged: colDef.codeValueChanged,
  };
  const formatOf = (config: any, params: any) =>
    typeof config.valueFormatter === "function"
      ? config.valueFormatter(params)
      : (params.value ?? "");

  // selector 가 undefined 를 돌려주면 ag-grid 는 colDef 의 cellEditor/cellRenderer 로 돌아간다.
  colDef.cellEditorSelector = (params: any) => {
    const config = rowConfig(params.data);
    return config
      ? { component: config.cellEditor ?? "agTextCellEditor" }
      : undefined;
  };
  // 코드박스는 cd_displaycolumn(이름 필드)을 그리므로 대체 타입 행은 자기 값으로 그린다.
  colDef.cellRendererSelector = (params: any) => {
    const config = rowConfig(params.data);
    if (!config) return undefined;
    return {
      component:
        config.cellRenderer ??
        ((p: any) => renderFindHighlight(p, formatOf(config, p))),
    };
  };

  colDef.valueFormatter = (params: any) => {
    const config = rowConfig(params.data);
    if (config) return formatOf(config, params);
    return typeof base.valueFormatter === "function"
      ? base.valueFormatter(params)
      : params.value;
  };

  colDef.valueParser = (params: any) => {
    const config = rowConfig(params.data);
    const parser = config ? config.valueParser : base.valueParser;
    return typeof parser === "function" ? parser(params) : params.newValue;
  };

  colDef.valueSetter = (params: any) => {
    const config = rowConfig(params.data);
    const setter = config ? config.valueSetter : base.valueSetter;
    if (typeof setter === "function") return setter(params);
    params.data[params.colDef.field] = params.newValue;
    return true;
  };

  // Excel 내보내기 날짜 셀 스타일(SnGridExcelStyleId.date) 등도 그 행의 타입을 따른다.
  colDef.cellClass = (params: any) => {
    const config = rowConfig(params.data);
    const cellClass = config ? config.cellClass : base.cellClass;
    return typeof cellClass === "function" ? cellClass(params) : cellClass;
  };

  colDef.getFindText = (params: any) => {
    const config = rowConfig(params.data);
    if (config) return formatOf(config, params);
    return typeof base.getFindText === "function"
      ? base.getFindText(params)
      : (params.value ?? "");
  };

  if (typeof base.codeValueChanged === "function") {
    colDef.codeValueChanged = (params: any) => {
      if (rowConfig(params.node?.data ?? params.data)) return;
      return base.codeValueChanged(params);
    };
  }
};

export const SnColumnsCreate = async (
  columns: ColumnConfig[],
  isEditable: boolean // editable 여부를 제어하는 콜백 함수
): Promise<CustomColDef[]> => {
  // 컬럼 설정이 아직 안 왔거나 등록되지 않은 화면이면 빈 컬럼으로 넘긴다.
  // 여기서 터지면 async라 잡히지 않고 화면 전체가 죽어 새로고침해야 한다.
  if (!Array.isArray(columns)) return [];

  const flatColumnDefs = await Promise.all(
    columns
      .sort((a, b) => a.no_seq - b.no_seq)
      .map(async (column) => {
        const typeConfig = await getColumnTypeConfig(column);
        const columnSpecificEditableFn =
          typeof typeConfig.editable === "function"
            ? typeConfig.editable
            : () => true;
        const colDef: any = {
          headerClass:
            column.yn_required === "Y" ? "required-header" : "normal-header", //  필수 입력값 컬럼이면 스타일 적용

          headerName: column.nm_column, // 컬럼 헤더
          aggFunc: column.yn_sum === "Y" ? "sum" : undefined, // 그룹했을 때 합계되도록
          field: column.cd_column, // 필드 이름
          initialWidth: column.qt_width, // 필드 크기
          width: column.qt_width, // 필드 크기
          hide: column.fg_visible !== "Y", // 표시 여부
          suppressColumnsToolPanel: column.fg_visible === "H", // 컬럼리스트에 안나오게
          sortable: column.yn_allowsorting === "Y", // 정렬 여부
          valueFormatter: column.valueFormatter, // 사용자 정의 값 포맷터
          pinned:
            column.yn_fixed === "Y" &&
            (column.tp_fixedposition === "left" ||
              column.tp_fixedposition === "right")
              ? (column.tp_fixedposition as "left" | "right")
              : null,
          columnConfig: {
            cd_grid: column.cd_grid,
            cd_menu: column.cd_menu,
            cd_column: column.cd_column,
            cd_displaycolumn: column.cd_displaycolumn,
            nm_column: column.nm_column,
            fg_visible: column.fg_visible,
            yn_drilldown: column.yn_drilldown,
            yn_sum: column.yn_sum,
            dc_editcon: column.dc_editcon,
            cd_popup: column.cd_popup,
            tp_column: column.tp_column,
            tp_format: column.tp_format,
            popup_apply: column.popup_apply,
            cd_clas: column.cd_clas,
            js_combo: column.js_combo,
            cd_value: column.cd_value,
            tpColumnByRow: column.tpColumnByRow,
          },
          ...typeConfig,
          // 화면에서 cellStyle 을 넘긴 컬럼만 타입 기본값을 덮는다.
          // (안 넘기면 기존 동작 그대로 — tp_column 별 기본 정렬이 유지된다)
          ...(column.cellStyle ? { cellStyle: column.cellStyle } : {}),
          editable: (params: any) => {
            if (!isEditable) {
              return false;
            }
            return columnSpecificEditableFn(params);
          },
          suppressNavigable: (params: any) =>
            params.node.rowPinned === "bottom", // 방향키로 풋터에 포커스 들어가지 못하도록
        };

        if (column.tpColumnByRow) {
          await applyRowTypeOverride(colDef, column);
        }

        // 그룹명 추가 (나중에 묶을 때 사용)
        return { ...colDef, _group: column.nm_group ?? "" };
      })
  );

  const groupMap = new Map<string, any[]>();

  for (const colDef of flatColumnDefs) {
    const groupName = colDef._group?.trim();
    if (groupName) {
      if (!groupMap.has(groupName)) groupMap.set(groupName, []);
      groupMap.get(groupName)!.push(colDef);
    }
  }
  // no_seq 순서를 유지한 채 조립한다. 그룹 컬럼을 만나면 그 그룹이 처음 등장한
  // 자리에 그룹 헤더를 통째로 끼워넣고, 이후 같은 그룹 컬럼은 건너뛴다.
  // (ag-Grid는 그룹의 children이 배열상 연속이어야 하므로, no_seq가 떨어져 있는
  //  같은 그룹 컬럼은 첫 등장 위치로 모인다.)
  const groupedColumnDefs: any[] = [];
  const emittedGroups = new Set<string>();

  for (const colDef of flatColumnDefs) {
    const groupName = colDef._group?.trim();
    if (!groupName) {
      groupedColumnDefs.push(colDef);
      continue;
    }
    if (emittedGroups.has(groupName)) continue;
    emittedGroups.add(groupName);
    groupedColumnDefs.push({
      headerName: groupName,
      marryChildren: true,
      children: groupMap.get(groupName)!.map(({ _group, ...child }) => child),
    });
  }

  return groupedColumnDefs;
};

/**
 * 코드박스 컬럼과 팝업 컬럼의 매핑({ popupCol: 팝업쪽 컬럼, target: 그리드쪽 컬럼 }).
 * 값 초기화(Delete)와 값 채우기(코드 직접 입력) 모두 이 매핑을 사용한다.
 *
 * 1) 편집 컬럼 === 팝업 기준 컬럼(popupCols[0], 예: cd_pjt)  → 팝업 컬럼 그대로 사용
 * 2) 편집 컬럼이 기준 컬럼 + 접미사 형태(cd_pjt_ref, cd_wh_isu) → 팝업 컬럼 전체에 같은
 *    접미사를 붙여서 매핑(cd_pjt→cd_pjt_ref, nm_pjt→nm_pjt_ref). 행 데이터에 실제로
 *    존재하는 컬럼만 대상으로 삼는다. 접미사를 안 붙였다가는 편집 불가한 원본 컬럼
 *    (cd_pjt, cd_wh)을 건드리게 된다.
 * 3) 그 외(cd_matl ↔ POPUP_ITEM 처럼 이름 규칙이 다른 경우) → 자기 자신과 표시 컬럼
 *    (cd_displaycolumn, 없으면 cd_ → nm_ 치환)만 매핑
 */
const getCodeBoxColumnMappings = (
  column: ColumnConfig,
  popupCols: string[],
  field: string | undefined,
  rowData: Record<string, any>
): { popupCol: string; target: string }[] => {
  if (!field || popupCols[0] === field) {
    return popupCols.map((col) => ({ popupCol: col, target: col }));
  }

  if (field.startsWith(popupCols[0])) {
    const suffix = field.slice(popupCols[0].length); // 예: "_ref", "_isu"
    const mappings = popupCols
      .map((col) => ({ popupCol: col, target: `${col}${suffix}` }))
      .filter(({ target }) => target === field || target in rowData);
    if (mappings.length > 0) return mappings;
  }

  const displayCol = column.cd_displaycolumn || field.replace("cd_", "nm_");
  return [
    { popupCol: popupCols[0], target: field },
    ...(popupCols[1] && displayCol !== field && displayCol in rowData
      ? [{ popupCol: popupCols[1], target: displayCol }]
      : []),
  ];
};

/**
 * 다른 컬럼의 필드 목록 — 도움창 · 코드조회가 건드리면 안 되는 필드.
 *
 * 상위 코드 컬럼: 이 도움창 결과(popupCols)에 자기 코드가 들어 있는 다른 코드박스 컬럼
 * (예: 거래처품목 도움창 결과에 cd_item 이 있으므로 품목 컬럼이 상위).
 * 상위 컬럼의 도움창 필드(코드 · 이름 · 규격 …)는 상위 컬럼의 값이므로
 * - 지울 때: 항상 남긴다 (거래처품목을 지우면 거래처품목 도움창에만 있는 필드만 지운다)
 * - 적용할 때(onlyLocked): 상위 컬럼이 이 행에서 수정 불가(dc_editcon)일 때만 남긴다
 *   (저장된 발주의 품목은 그대로, 신규행은 거래처품목 값으로 덮어쓴다)
 */
export const getParentFields = (
  api: any,
  node: any,
  field: string | undefined,
  popupCols: string[],
  onlyLocked: boolean
): Set<string> => {
  const locked = new Set<string>();
  const data = node?.data ?? {};
  for (const col of api?.getColumns?.() ?? []) {
    const colDef: any = col.getColDef();
    const parentField = colDef?.field;
    const config = colDef?.columnConfig;
    if (!parentField || parentField === field || !config?.cd_popup) continue;
    if (!popupCols.includes(parentField)) continue;
    const tp = getRowTpColumn(colDef, data);
    if (tp !== "SnCodeBox" && tp !== "SnMultiCodeBox") continue;
    if (onlyLocked && col.isCellEditable(node)) continue;
    const parentCols = PopupSettings[config.cd_popup]?.columns ?? [];
    getCodeBoxColumnMappings(config, parentCols, parentField, data).forEach(
      ({ target }) => locked.add(target)
    );
  }
  return locked;
};

/**
 * 코드박스에 직접 친 값으로 원본을 찾는다.
 *
 * 못 찾는 것은 오류가 아니라 "입력값이 코드가 아니었다"는 뜻이므로 조회는 silentError로
 * 부른다. 예전에는 조회 쿼리가 던진 오류(예: 품목코드가 존재하지 않습니다)가 그대로
 * 오류창으로 떴고, fnGetDataSet이 undefined를 돌려주는 바람에 뒤따르는 정리 로직도 끊겼다.
 *
 * @param searchValue 조회에 쓸 값. 넘기지 않으면 params.newValue를 쓴다(앞뒤 공백 제거).
 * @returns 찾은 행 목록. 못 찾거나 실패하면 빈 배열 — 호출부가 관련 컬럼을 비운다.
 */
export const setCodeBoxValue = async (
  column: ColumnConfig,
  params: any,
  searchValue?: string
) => {
  try {
    const code = column.cd_popup;
    const cd_clas = column.cd_clas;
    const newValue = (searchValue ?? String(params.newValue ?? "")).trim();
    if (!code || newValue === "") return [];

    const popupSetting = PopupSettings[code];
    if (!popupSetting) return [];

    const paramData = params.data; // 26.07.22 kdy 복합PK의 경우때문에

    // 26.07.22 kdy 복합 PK 고려
    // 행에도 존재하고 가져온 데이터에도 존재할 때
    const pickRows = (data: any): any[] => {
      const rows = Array.isArray(data?.[0]) ? data[0] : [];
      if (!paramData.cd_biz) return rows;
      return rows.filter(
        (item: any) => !("cd_biz" in item) || item.cd_biz === paramData.cd_biz
      );
    };

    // PopupSettings.filterParams 로 지정한 팝업만 행의 해당 값을 조회 조건으로 넘긴다
    // (예: 거래처품목은 행의 거래처 · 품목으로 거른다. 지정하지 않은 팝업은 파라미터가 예전과 같다)
    const rowParams = Object.fromEntries(
      (popupSetting.filterParams ?? []).map((key: string) => [
        key,
        paramData?.[key] ?? "",
      ])
    );

    const param1 = {
      id: code.toLowerCase() + "_02",
      params: {
        ...rowParams,
        cd_firm: global.getState().userInfo.cd_firm,
        [popupSetting.str_search]: newValue,
        str_search: newValue,
        cd_clas: cd_clas,
        cd_code: paramData.cd_code,
      },
    };

    const rows1 = pickRows(await fnGetDataSet(param1, { silentError: true }));
    if (rows1.length === 1) {
      return rows1;
    }

    const param2 = {
      id: code.toLowerCase() + "_03",
      params: {
        ...rowParams,
        cd_firm: global.getState().userInfo.cd_firm,
        [popupSetting.str_search]: newValue,
        str_search: newValue,
        cd_clas: cd_clas,
        cd_code: paramData.cd_code,
      },
    };

    const rows2 = pickRows(await fnGetDataSet(param2, { silentError: true }));
    if (rows2.length === 1) {
      return rows2;
    }
    return [];
  } catch (e) {
    return [];
  }
};
